import React, { useState, useRef } from 'react';
import { motion } from 'framer-motion';
import {
  SiWebpack,
  SiReact,
  SiVuedotjs,
  SiAngular,
  SiNextdotjs,
  SiTypescript,
  SiTailwindcss,
  SiNodedotjs,
  SiPython,
  SiPhp,
  SiFlutter,
  SiSwift,
  SiAmazon,
  SiDocker,
  SiKubernetes,
  SiExpress,
  SiDjango,
  SiSpring,
  SiLaravel,
  SiIonic,
  SiExpo,
  SiGooglecloud,
  SiFirebase,
  SiHeroku,
  SiVercel,
  SiSass
} from "react-icons/si";
import {
  FaJava,
  FaMicrosoft,
  FaCode
} from "react-icons/fa";

interface Benefit {
  id: number;
  title: string;
  description: string;
  icon: string;
}

interface TechCategory {
  name: string;
  technologies: string[];
}

const About: React.FC = () => {
  const [activeTechTab, setActiveTechTab] = useState('Frontend');
  const sectionRef = useRef<HTMLElement>(null);
  const cardsRef = useRef<HTMLDivElement>(null);

  const benefits: Benefit[] = [
    {
      id: 1,
      title: 'Expert Team',
      description: 'Our team consists of experienced developers, designers, and strategists',
      icon: '👥'
    },
    {
      id: 2,
      title: 'Agile Methodology',
      description: 'We follow agile development practices for faster delivery',
      icon: '⚡'
    },
    {
      id: 3,
      title: '24/7 Support',
      description: 'Round-the-clock support to ensure your project runs smoothly',
      icon: '🛠️'
    },
    {
      id: 4,
      title: 'Quality Assurance',
      description: 'Rigorous testing and quality checks before every release',
      icon: '✅'
    },
    {
      id: 5,
      title: 'Scalable Solutions',
      description: 'Build for today, scale for tomorrow with our architecture',
      icon: '📈'
    },
    {
      id: 6,
      title: 'Competitive Pricing',
      description: 'Transparent pricing with no hidden costs or surprises',
      icon: '💰'
    }
  ];

  const techCategories: TechCategory[] = [
    {
      name: 'Frontend',
      technologies: ['React', 'Vue.js', 'Angular', 'Next.js', 'TypeScript', 'Tailwind CSS', 'Sass', 'Webpack']
    },
    {
      name: 'Backend',
      technologies: ['Node.js', 'Python', 'Java', 'PHP', 'Express.js', 'Django', 'Spring Boot', 'Laravel']
    },
    {
      name: 'Mobile',
      technologies: ['React Native', 'Flutter', 'Swift', 'Kotlin', 'Ionic', 'Xamarin', 'Cordova', 'Expo']
    },
    {
      name: 'Cloud',
      technologies: ['AWS', 'Google Cloud', 'Azure', 'Docker', 'Kubernetes', 'Firebase', 'Heroku', 'Vercel']
    }
  ];

  const getTechIcon = (tech: string) => {
    switch (tech) {
      // Frontend
      case 'React': return <SiReact className="w-6 h-6 text-blue-500" />;
      case 'Vue.js': return <SiVuedotjs className="w-6 h-6 text-green-500" />;
      case 'Angular': return <SiAngular className="w-6 h-6 text-red-500" />;
      case 'Next.js': return <SiNextdotjs className="w-6 h-6 text-black dark:text-white" />;
      case 'TypeScript': return <SiTypescript className="w-6 h-6 text-blue-600" />;
      case 'Tailwind CSS': return <SiTailwindcss className="w-6 h-6 text-cyan-500" />;
      case 'Sass': return <SiSass className="w-6 h-6 text-pink-500" />;
      case 'Webpack': return <SiWebpack className="w-6 h-6 text-blue-400" />;

      // Backend
      case 'Node.js': return <SiNodedotjs className="w-6 h-6 text-green-600" />;
      case 'Python': return <SiPython className="w-6 h-6 text-yellow-500" />;
      case 'Java': return <FaJava className="w-6 h-6 text-orange-500" />;
      case 'PHP': return <SiPhp className="w-6 h-6 text-purple-500" />;
      case 'Express.js': return <SiExpress className="w-6 h-6 text-gray-600 dark:text-gray-300" />;
      case 'Django': return <SiDjango className="w-6 h-6 text-green-700" />;
      case 'Spring Boot': return <SiSpring className="w-6 h-6 text-green-500" />;
      case 'Laravel': return <SiLaravel className="w-6 h-6 text-red-600" />;

      // Mobile
      case 'React Native': return <SiReact className="w-6 h-6 text-blue-500" />;
      case 'Flutter': return <SiFlutter className="w-6 h-6 text-blue-400" />;
      case 'Swift': return <SiSwift className="w-6 h-6 text-orange-500" />;
      case 'Kotlin': return <FaCode className="w-6 h-6 text-purple-600" />;
      case 'Ionic': return <SiIonic className="w-6 h-6 text-blue-600" />;
      case 'Xamarin': return <FaMicrosoft className="w-6 h-6 text-blue-600" />;
      case 'Cordova': return <FaCode className="w-6 h-6 text-gray-600 dark:text-gray-300" />;
      case 'Expo': return <SiExpo className="w-6 h-6 text-gray-700 dark:text-gray-200" />;

      // Cloud
      case 'AWS': return <SiAmazon className="w-6 h-6 text-orange-500" />;
      case 'Google Cloud': return <SiGooglecloud className="w-6 h-6 text-blue-500" />;
      case 'Azure': return <FaMicrosoft className="w-6 h-6 text-blue-600" />;
      case 'Docker': return <SiDocker className="w-6 h-6 text-blue-500" />;
      case 'Kubernetes': return <SiKubernetes className="w-6 h-6 text-blue-600" />;
      case 'Firebase': return <SiFirebase className="w-6 h-6 text-yellow-500" />;
      case 'Heroku': return <SiHeroku className="w-6 h-6 text-purple-500" />;
      case 'Vercel': return <SiVercel className="w-6 h-6 text-black dark:text-white" />;

      default: return <div className="w-6 h-6 bg-gray-400 rounded flex items-center justify-center text-xs font-bold text-white">?</div>;
    }
  };

  return (
    <section ref={sectionRef} id="about" className="py-10 dark:bg-navy bg-slate-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl lg:text-5xl font-bold dark:text-white text-gray-900 mb-6">
            About <span className="text-transparent bg-clip-text bg-gradient-to-r from-accent to-accent-light">Us</span>
          </h2>
          <p className="text-xl dark:text-gray-300 text-gray-600 max-w-3xl mx-auto">
            We are a team of passionate developers and designers dedicated to creating
            exceptional digital experiences that drive business growth.
          </p>
        </div>

        {/* CEO Section */}
        <div className="mb-20">
          <div ref={cardsRef} className="grid grid-cols-1 md:grid-cols-2 gap-8 overflow-hidden">
            <motion.div
              initial={{ x: -100, opacity: 0 }}
              whileInView={{ x: 0, opacity: 1 }}
              viewport={{ once: false, margin: "-50px" }}
              transition={{ duration: 0.6, delay: 0, ease: "easeOut" }}
            >
              <div className="glass rounded-2xl p-6 text-center">
                <div className="w-20 h-20 bg-gradient-to-br from-accent to-accent-light rounded-full flex items-center justify-center text-2xl font-bold text-white mx-auto mb-4">
                  NM
                </div>
                <h3 className="text-xl font-bold dark:text-white text-text-gray-900 mb-2">Narayana Murthy</h3>
                <p className="text-accent-light mb-4">CEO & Founder</p>
                <p className="dark:text-gray-300 text-text-gray-600 text-sm leading-relaxed">
                  A dynamic leader with 15+ years of expertise in software engineering and business strategy, driving innovation at the intersection of technology and enterprise growth.
                </p>
              </div>
            </motion.div>

            <motion.div
              initial={{ x: 100, opacity: 0 }}
              whileInView={{ x: 0, opacity: 1 }}
              viewport={{ once: false, margin: "-50px" }}
              transition={{ duration: 0.6, delay: 0, ease: "easeOut" }}
            >
              <div className="glass rounded-2xl p-9 text-center">
                <div className="w-20 h-20 bg-gradient-to-br from-accent to-accent-light rounded-full flex items-center justify-center text-2xl font-bold text-white mx-auto mb-4">
                  SK
                </div>
                <h3 className="text-xl font-bold dark:text-white text-text-gray-900 mb-2">Vijaya Kumar</h3>
                <p className="text-accent-light mb-4">Co-Founder</p>
                <p className="dark:text-gray-300 text-text-gray-600 text-sm leading-relaxed">
                  A forward-thinking leader with over 15 years of experience uniting technology and strategy to engineer scalable, high-impact business solutions.
                </p>
              </div>
            </motion.div>

            {/* <motion.div
              initial={{ y: 60, opacity: 0, scale: 0.9 }}
              whileInView={{ y: 0, opacity: 1, scale: 1 }}
              viewport={{ once: false, margin: "-50px" }}
              transition={{ duration: 0.6, delay: 0.3, ease: "easeOut" }}
            > */}
            <motion.div
              initial={{ x: -100, opacity: 0 }}
              whileInView={{ x: 0, opacity: 1 }}
              viewport={{ once: false, margin: "-50px" }}
              transition={{ duration: 0.6, delay: 0, ease: "easeOut" }}
            >

              <div className="glass rounded-2xl p-8 text-center">
                <div className="w-20 h-20 bg-gradient-to-br from-accent to-accent-light rounded-full flex items-center justify-center text-2xl font-bold text-white mx-auto mb-4">
                  RD
                </div>
                <h3 className="text-xl font-bold dark:text-white text-text-gray-900 mb-2">Raja Duraisamy</h3>
                <p className="text-accent-light mb-4">Co-Founder</p>
                <p className="dark:text-gray-300 text-text-gray-600 text-sm leading-relaxed">
                  A technology leader with 20+ years of experience, he has architected scalable, transformative solutions that empower businesses to evolve and innovate.
                </p>
              </div>
            </motion.div>

            <motion.div
              initial={{ x: 100, opacity: 0 }}
              whileInView={{ x: 0, opacity: 1 }}
              viewport={{ once: false, margin: "-50px" }}
              transition={{ duration: 0.6, delay: 0, ease: "easeOut" }}
            >
              <div className="glass rounded-2xl p-6 text-center">
                <div className="w-20 h-20 bg-gradient-to-br from-accent to-accent-light rounded-full flex items-center justify-center text-2xl font-bold text-white mx-auto mb-4">
                  SK
                </div>
                <h3 className="text-xl font-bold dark:text-white text-text-gray-900 mb-2">Satish Kumar</h3>
                <p className="text-accent-light mb-4">Co-Founder</p>
                <p className="dark:text-gray-300 text-text-gray-600 text-sm leading-relaxed">
                  A visionary leader with 20+ years of architectural expertise, he unites creativity and technology to craft exceptional user experiences and intelligent interface designs
                </p>
              </div>
            </motion.div>
          </div>
        </div>

        {/* Mission Section */}
        <div className="mb-20">
          <div>
            <div className="glass rounded-2xl p-8 max-w-4xl mx-auto">
              <h3 className="text-2xl font-bold dark:text-white text-text-gray-900 mb-6">Our Mission</h3>
              <p className="dark:text-gray-300 text-text-gray-600 leading-relaxed mb-6">
                To empower businesses with cutting-edge software solutions that not only
                meet their current needs but also position them for future growth and success.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex items-center">
                  <span className="w-2 h-2 bg-accent rounded-full mr-3"></span>
                  <span className="dark:text-gray-300 text-text-gray-600">Innovation-driven development</span>
                </div>
                <div className="flex items-center">
                  <span className="w-2 h-2 bg-accent rounded-full mr-3"></span>
                  <span className="dark:text-gray-300 text-text-gray-600">Client-centric approach</span>
                </div>
                <div className="flex items-center">
                  <span className="w-2 h-2 bg-accent rounded-full mr-3"></span>
                  <span className="dark:text-gray-300 text-text-gray-600">Continuous learning and improvement</span>
                </div>
                <div className="flex items-center">
                  <span className="w-2 h-2 bg-accent rounded-full mr-3"></span>
                  <span className="dark:text-gray-300 text-text-gray-600">Long-term partnership focus</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Why Choose Us */}
        <div className="mb-20">
          <h3 className="text-3xl font-bold dark:text-white text-text-gray-900 text-center mb-12">
            Why Choose <span className="text-transparent bg-clip-text bg-gradient-to-r from-accent to-accent-light">Us?</span>
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {benefits.slice(0, 3).map((benefit, index) => (
              <div
                key={benefit.id}
                className=""
                style={{ transitionDelay: `${index * 0.1}s` }}
              >
                <div className="glass rounded-2xl p-6 h-full hover:bg-opacity-30 transition-all duration-300 group">
                  <div className="text-4xl mb-4 group-hover:scale-110 transition-transform duration-300">
                    {benefit.icon}
                  </div>
                  <h4 className="text-xl font-semibold dark:text-white text-text-gray-900 mb-3 group-hover:text-accent-light transition-colors duration-300">
                    {benefit.title}
                  </h4>
                  <p className="dark:text-gray-300 text-text-gray-600 text-sm leading-relaxed">
                    {benefit.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Technologies */}
        <div>
          <h3 className="text-2xl lg:text-3xl font-bold dark:text-white text-text-gray-900 text-center mb-8 lg:mb-12">
            Our <span className="text-transparent bg-clip-text bg-gradient-to-r from-accent to-accent-light">Tech Stack</span>
          </h3>

          {/* Tech Tabs */}
          <div className="flex flex-wrap justify-center gap-2 lg:gap-4 mb-6 lg:mb-8 px-4">
            {techCategories.map((category) => (
              <button
                key={category.name}
                onClick={() => setActiveTechTab(category.name)}
                className={`px-4 lg:px-6 py-2 lg:py-3 rounded-full font-semibold transition-all duration-300 text-sm lg:text-base ${activeTechTab === category.name
                  ? 'bg-accent text-white shadow-lg shadow-accent/25'
                  : 'glass dark:text-gray-300 text-text-gray-600 hover:dark:text-white hover:text-text-gray-900 hover:bg-opacity-30'
                  }`}
              >
                {category.name}
              </button>
            ))}
          </div>

          {/* Tech Grid */}
          <div className="glass rounded-2xl p-4 lg:p-8">
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-3 lg:gap-4">
              {techCategories
                .find(category => category.name === activeTechTab)
                ?.technologies.map((tech, index) => (
                  <div
                    key={tech}
                    className="text-center group cursor-pointer"
                    style={{ animationDelay: `${index * 0.05}s` }}
                  >
                    <div className="glass rounded-xl p-3 lg:p-4 hover:bg-opacity-30 transition-all duration-300 group-hover:scale-105 group-hover:-translate-y-1">
                      <div className="flex justify-center mb-2 group-hover:text-accent-light transition-colors duration-300">
                        {getTechIcon(tech)}
                      </div>
                      <div className="text-xs lg:text-sm font-semibold dark:text-gray-300 text-text-gray-600 group-hover:dark:text-white group-hover:text-text-gray-900 transition-colors duration-300">
                        {tech}
                      </div>
                    </div>
                  </div>
                ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;
